import { defineStore } from 'pinia'

export const useUIStore = defineStore('ui', () => {
  const search = ref(false)
  const menu = ref(false)
  const sidebar = ref(false)
  const mobile = ref(false)
  const holidayAnimation = ref(false)
  const theme = ref('ovatu')
  const panel = ref({ mode: 'panel' })
  const schedule = ref({ showResources: 'all', sectionSize: 200 })
  const alerts = ref({ filter: false })
  const dashboard = ref({ showPastReservations: false })
  const order = ref({ print: 'thermal' })
  const width = ref<number | null>(null)
  const maintenance = ref<any>(null)
  const hideBanner = ref(false)
  const hideHolidayBanner = ref(false)
  const privacyMode = ref(false)
  const settingsMenuCollapsed = ref(false)

  /**
   * Width of the left-hand nav column in pixels. Shared between the main
   * menu and the settings menu so toggling between them doesn't bounce the
   * content area. Restored from localStorage on first read; subsequent
   * writes (via the resize drag handle in `layouts/default.vue`) persist
   * back. Clamped to [NAV_WIDTH_MIN, NAV_WIDTH_MAX].
   */
  const NAV_WIDTH_MIN = 200
  const NAV_WIDTH_MAX = 480
  const NAV_WIDTH_DEFAULT = 240
  const navWidth = ref<number>(NAV_WIDTH_DEFAULT)

  if (import.meta.client) {
    const stored = Number(localStorage.getItem('ovatu.navWidth'))
    if (Number.isFinite(stored) && stored >= NAV_WIDTH_MIN && stored <= NAV_WIDTH_MAX) {
      navWidth.value = stored
    }
    settingsMenuCollapsed.value = localStorage.getItem('ovatu.settingsMenuCollapsed') === 'true'
  }

  const isMobile = computed(() => (width.value ?? 0) < 980)

  const toggleMobile = () => { mobile.value = !mobile.value }
  const toggleMenu = () => { menu.value = !menu.value }
  const toggleSidebar = () => { sidebar.value = !sidebar.value }
  const toggleSearch = () => { search.value = !search.value }
  const toggleHolidayAnimation = () => { holidayAnimation.value = !holidayAnimation.value }
  const togglePrivacyMode = () => { privacyMode.value = !privacyMode.value }
  const toggleSettingsMenu = () => {
    settingsMenuCollapsed.value = !settingsMenuCollapsed.value
    if (import.meta.client) {
      try { localStorage.setItem('ovatu.settingsMenuCollapsed', String(settingsMenuCollapsed.value)) } catch {}
    }
  }

  const setMobile = (val: boolean) => { mobile.value = val }
  const setSearch = (val: boolean) => { search.value = val }
  const setWidth = (val: number | null) => { width.value = val }
  const setSchedule = ({ key, value }: { key: string; value: any }) => { (schedule.value as any)[key] = value }
  const setAlerts = ({ key, value }: { key: string; value: any }) => { (alerts.value as any)[key] = value }
  const setDashboard = ({ key, value }: { key: string; value: any }) => { (dashboard.value as any)[key] = value }
  const setOrder = ({ key, value }: { key: string; value: any }) => { (order.value as any)[key] = value }
  const setMenu = (val: boolean) => { menu.value = val }
  const setSidebar = (val: boolean) => { sidebar.value = val }
  const setMaintenance = (val: any) => { maintenance.value = val }
  const setTheme = (val: string) => { theme.value = val }
  const setHideBanner = (val: boolean) => { hideBanner.value = val }
  const setHideHolidayBanner = (val: boolean) => { hideHolidayBanner.value = val }
  const setHolidayAnimation = (val: boolean) => { holidayAnimation.value = val }
  const setPrivacyMode = (val: boolean) => { privacyMode.value = val }

  const setNavWidth = (val: number) => {
    const clamped = Math.max(NAV_WIDTH_MIN, Math.min(NAV_WIDTH_MAX, Math.round(val)))
    navWidth.value = clamped
    if (import.meta.client) {
      try { localStorage.setItem('ovatu.navWidth', String(clamped)) } catch {}
    }
  }

  return {
    search,
    menu,
    sidebar,
    mobile,
    holidayAnimation,
    theme,
    panel,
    schedule,
    alerts,
    dashboard,
    order,
    width,
    maintenance,
    hideBanner,
    hideHolidayBanner,
    privacyMode,
    settingsMenuCollapsed,
    navWidth,
    NAV_WIDTH_MIN,
    NAV_WIDTH_MAX,
    isMobile,
    toggleMobile,
    toggleMenu,
    toggleSidebar,
    toggleSearch,
    toggleHolidayAnimation,
    togglePrivacyMode,
    toggleSettingsMenu,
    setMobile,
    setSearch,
    setWidth,
    setSchedule,
    setAlerts,
    setDashboard,
    setOrder,
    setMenu,
    setSidebar,
    setMaintenance,
    setTheme,
    setHideBanner,
    setHideHolidayBanner,
    setHolidayAnimation,
    setPrivacyMode,
    setNavWidth,
  }
})
